/**
 * @module slideshow-loader
 * Manages video players (Vimeo, YouTube, local) to add functionality
 * like fade-in on play. It dynamically loads the YouTube Iframe API if needed.
 */

// --- YouTube API Loader ---
let youtubeApiReady = false;
let youtubeApiLoading = false;
const youtubeCallbacks = [];

function loadYouTubeApi() {
    if (youtubeApiReady || youtubeApiLoading) return;

    youtubeApiLoading = true;
    const tag = document.createElement('script');
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
        youtubeApiReady = true;
        youtubeApiLoading = false;
        youtubeCallbacks.forEach(callback => callback());
        youtubeCallbacks.length = 0; // Clear callbacks after execution
    };
}

function onYouTubeApiReady(callback) {
    if (youtubeApiReady) {
        callback();
    } else {
        youtubeCallbacks.push(callback);
        if (!youtubeApiLoading) {
            loadYouTubeApi();
        }
    }
}

/**
 * Sets up an IntersectionObserver to play/pause a video when it enters/leaves the viewport.
 * @param {HTMLElement} element - The element to observe.
 * @param {Function} playFn - The function to call when the element is intersecting.
 * @param {Function} pauseFn - The function to call when the element is not intersecting.
 */
function setupViewportObserver(element, playFn, pauseFn) {
    const observerOptions = {
        root: null, // relative to the viewport
        threshold: 0, // trigger as soon as any part of the element is visible
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                playFn();
            } else {
                pauseFn();
            }
        });
    }, observerOptions);

    observer.observe(element);
}

/**
 * Initializes video players within a given scope, handling hover-to-play based on the
 * `data-display="hover"` attribute of the video container.
 * @param {HTMLElement|Document} [scope=document] - The element to search within for videos.
 */
export function initializeVideoPlayers(scope = document) {
    const videoContainers = scope.querySelectorAll('.video-container');

    videoContainers.forEach(container => {
        const iframe = container.querySelector('iframe');  // Potential iframe video
        const video = container.querySelector('video');    // Potential HTML5 video element
        const isHoverPlay = container.closest('.media').dataset.videoDisplay === 'hover';  // Check for hover play attribute

        // --- Handle Iframe Videos (Vimeo & YouTube) ---
        if (iframe) {
          // --- Vimeo Iframe ---
          if (iframe.src.includes('vimeo.com') && typeof window.Vimeo !== 'undefined') {
              const player = new window.Vimeo.Player(iframe);

              // Check initial state in case it's already playing (handles race condition for autoplay)
              player.getPaused().then(paused => {
                  if (!paused) {
                      iframe.setAttribute('data-playing', 'true');
                  }
              }).catch(() => {
                  // Ignore errors if player isn't ready, the event listener will catch it.
              });

              player.on('play', () => iframe.setAttribute('data-playing', 'true'));
              player.on('pause', () => iframe.removeAttribute('data-playing'));
              // Also handle the 'ended' state for non-looping videos
              player.on('ended', () => iframe.removeAttribute('data-playing'));

              if (isHoverPlay) {
                  let pauseTimeout;
                  container.addEventListener('mouseenter', () => {
                      clearTimeout(pauseTimeout);
                      player.play();
                  });
                  container.addEventListener('mouseleave', () => {
                      iframe.removeAttribute('data-playing');
                      pauseTimeout = setTimeout(() => player.pause(), 1000);
                  });
                  player.pause();
                  iframe.removeAttribute('data-playing');
              } else {
                  // For autoplaying background videos, PHP adds autoplay=1.
                  // The initial state check and event listeners are sufficient.
                  // We just ensure it's muted via the API as a fallback.
                  player.setVolume(0);
              }
          }
          // --- YouTube Iframe ---
          else if (iframe.src.includes('youtube.com')) {
              onYouTubeApiReady(() => {
                  const player = new window.YT.Player(iframe, {
                      events: {
                          'onReady': (event) => {
                              event.target.mute();
                              if (isHoverPlay) {
                                  let pauseTimeout;
                                  container.addEventListener('mouseenter', () => {
                                      clearTimeout(pauseTimeout);
                                      event.target.playVideo();
                                  });
                                  container.addEventListener('mouseleave', () => {
                                      iframe.removeAttribute('data-playing');
                                      pauseTimeout = setTimeout(() => event.target.pauseVideo(), 1000);
                                  });
                              } else {
                                  // For autoplaying background videos, PHP adds autoplay=1.
                                  // The onStateChange event handles the fade-in, so we just need to ensure it plays.
                                  // The observer is removed to avoid conflict with autoplay=1.
                                  event.target.playVideo();
                              }
                          },
                          'onStateChange': (event) => {
                              if (event.data === window.YT.PlayerState.PLAYING) {
                                  iframe.setAttribute('data-playing', 'true');
                              } else {
                                  iframe.removeAttribute('data-playing');
                              }
                          }
                      }
                  });
              });
          }
      }

      // --- Handle HTML5 <video> Element ---
        if (video) {
            video.addEventListener('play', () => video.setAttribute('data-playing', 'true'));
            video.addEventListener('pause', () => video.removeAttribute('data-playing'));
            video.addEventListener('ended', () => video.removeAttribute('data-playing'));

            // Check initial state in case it's already playing via `autoplay` attribute
            if (!video.paused) {
                video.setAttribute('data-playing', 'true');
            }

          if (isHoverPlay) {
  
              container.addEventListener('mouseenter', () => {
                  clearTimeout(pauseTimeout);
                  video.play();
              });
  
              container.addEventListener('mouseleave', () => {
                  video.removeAttribute('data-playing');
                  pauseTimeout = setTimeout(() => {
                      video.pause();
                  }, 1000);
              });
  
              // Initially pause and ensure not marked as playing for hover-to-play
              video.pause();
              video.removeAttribute('data-playing');
          } else {
              // For autoplaying background videos, the `autoplay` attribute is handled by the browser.
              // The event listeners and initial state check above are sufficient.
          }
        }
    });
}
